/**
 * UserSearch Module - Consolidated Enhanced Version
 * Self-contained user search component with multiselect filtering and programmatic control
 */
class UserSearch {
  static EL_CONTAINER = '.user-search-container';
  static EL_MODAL = '#user-search-modal';
  static EL_SEARCH_INPUT = '.user-search-input';
  static EL_ROLE_SELECT = '.user-search-role';
  static EL_GENRE_SELECT = '.user-search-genre';
  static EL_COMPANY_SELECT = '.user-search-company';
  static EL_RESULTS_TABLE = '.user-search-results';
  static EL_PAGINATION = '.user-search-pagination';
  static EL_SELECTED_COUNT = '.user-search-selected-count';
  static EL_CHECK_ALL = '.user-search-check-all';
  static EL_SELECTED_TABLE = '.user-search-selected-table';
  static EL_CLEAR_ALL = '.user-search-clear-all';

  constructor (options = {}) {
    this.options = {
      isModal: false,
      container: null,
      onSelect: null,
      onDeselect: null,
      pageSize: 25,
      showSelectedSection: true,
      autoRender: true,
      ...options,
    };

    this.selectedUsers = new Map();
    this.checkedResults = new Set(); // Track checked users in results table
    this.checkedSelected = new Set(); // Track checked users in selected table
    this.currentPage = 1;
    this.totalPages = 1;
    this.totalResults = 0;
    this.sortBy = 'name';
    this.sortDir = 'asc';
    this.lastClickedRow = null;

    // Selected users pagination
    this.selectedCurrentPage = 1;
    this.selectedTotalPages = 1;
    this.selectedPageSize = 10;
    this.searchParams = {
      query: '',
      roles: [],
      companies: [],
      genres: [],
      genreLogic: 'or',
      active: true,
    };

    this.multiselectInstances = {
      roles: null,
      companies: null,
      genres: null,
    };

    if (this.options.autoRender) {
      this.initElements();
      this.bindEvents();
      this.loadInitialData();
    }
  }

  // Static factory method for easy instantiation
  static create (container, options = {}) {
    return new UserSearch({
      container: typeof container === 'string' ? document.querySelector(container) : container,
      ...options,
    });
  }

  initElements () {
    if (this.options.isModal) {
      this.container = document.querySelector(UserSearch.EL_MODAL);
      if (!this.container) {
        this.createModal();
      }
    } else {
      this.container = this.options.container || document.querySelector(UserSearch.EL_CONTAINER);

      if (this.container && !this.container.querySelector('.user-search-inner')) {
        this.container.innerHTML = this.getSearchTemplate();
      }
    }

    if (!this.container) return;

    // Query elements after potentially creating the interface
    this.searchInput = this.container.querySelector(UserSearch.EL_SEARCH_INPUT);
    this.roleSelect = this.container.querySelector(UserSearch.EL_ROLE_SELECT);
    this.genreSelect = this.container.querySelector(UserSearch.EL_GENRE_SELECT);
    this.companySelect = this.container.querySelector(UserSearch.EL_COMPANY_SELECT);
    this.resultsTable = this.container.querySelector(UserSearch.EL_RESULTS_TABLE);
    this.pagination = this.container.querySelector(UserSearch.EL_PAGINATION);
    this.selectedCount = this.container.querySelector(UserSearch.EL_SELECTED_COUNT);
    this.checkAll = this.container.querySelector(UserSearch.EL_CHECK_ALL);
    this.selectedTable = this.container.querySelector(UserSearch.EL_SELECTED_TABLE);
    this.clearAllBtn = this.container.querySelector(UserSearch.EL_CLEAR_ALL);
    this.activeToggle = this.container.querySelector('.user-search-active-toggle');

    // New button references
    this.addSelectedBtn = this.container.querySelector('.add-selected-btn');
    this.addAllResultsBtn = this.container.querySelector('.add-all-results-btn');
    this.removeSelectedBtn = this.container.querySelector('.remove-selected-btn');
    this.selectedCheckAll = this.container.querySelector('.selected-check-all');
    this.genreLogicInputs = this.container.querySelectorAll('input[name="genre-logic"]');
  }

  createModal () {
    const modalHtml = `
      <div id="user-search-modal" class="reveal user-search-modal" data-reveal>
        <div class="modal-header">
          <h3>Search Users</h3>
          <button class="close-button" data-close aria-label="Close modal" type="button">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body">
          ${this.getSearchTemplate()}
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHtml);
    this.container = document.querySelector(UserSearch.EL_MODAL);

    if (typeof Foundation !== 'undefined') {
      new Foundation.Reveal($(this.container));
    }
  }

  getSearchTemplate () {
    return `
      <style>
        .user-search-results tbody tr.checked-row {
          background-color: #e8f5e9 !important;
        }
        .user-search-selected-table tbody tr.checked-row {
          background-color: #ffebee !important;
        }
      </style>
      <div class="user-search-inner">
        <div class="user-search-filters">
          <div class="grid-x grid-margin-x">
            <!-- Top row: Search + Company -->
            <div class="cell large-6 medium-6">
              <label class="inline-label">Search:
                <button type="button" class="filter-clear" data-filter="search" title="Clear" style="display: none;"><i class="fas fa-times"></i></button>
                <input type="text" class="user-search-input" placeholder="Name or email...">
              </label>
            </div>
            <div class="cell large-6 medium-6">
              <label class="inline-label">Company:
                <button type="button" class="filter-clear" data-filter="companies" title="Clear" style="display: none;"><i class="fas fa-times"></i></button>
                <select class="user-search-company" multiple data-placeholder="All Companies"></select>
              </label>
            </div>

            <!-- Bottom row: Role + Genre -->
            <div class="cell large-6 medium-6">
              <label class="inline-label">Role:
                <button type="button" class="filter-clear" data-filter="roles" title="Clear" style="display: none;"><i class="fas fa-times"></i></button>
                <select class="user-search-role" multiple data-placeholder="All Roles"></select>
              </label>
            </div>
            <div class="cell large-6 medium-6">
              <div class="genre-filter-wrapper">
              <div class="grid-x">
              <div class="cell small-12">
                <label class="inline-label">Genre:
                  <button type="button" class="filter-clear" data-filter="genres" title="Clear" style="display: none;"><i class="fas fa-times"></i></button>
                  <select class="user-search-genre" multiple data-placeholder="All Genres"></select>
                </label>
              </div>
              <div class="cell small-3 hide">
                <div class="genre-logic-toggle">
                    <label class="radio-inline" style="width: 50%;display: inline-block"><input type="radio" name="genre-logic" value="or" checked> OR</label>
                    <label class="radio-inline" style="width: 50%;display: inline-block"><input type="radio" name="genre-logic" value="and"> AND</label>
                  </div>

                </div>
              </div>
            </div>
          </div>

          <!-- Controls row - hide active toggle -->
          <div class="grid-x grid-margin-x align-middle" style="margin-top: 10px; display: none;">
            <div class="cell auto">
              <label class="toggle-label">
                <input type="checkbox" class="user-search-active-toggle" checked>
                <span>Active Users Only</span>
              </label>
            </div>
          </div>
        </div>


        <div class="user-search-results-wrapper">
          <div class="search-results-actions" style="display: flex; justify-content: flex-end; gap: 1rem; margin-bottom: 1rem;">
            <button type="button" class="button small success add-selected-btn" style="display: none;">
              <i class="fas fa-plus"></i> Add Selected (<span class="selected-count">0</span>)
            </button>
            <button type="button" class="button small secondary add-all-results-btn">
              <i class="fas fa-plus"></i> Add All Results (<span class="all-results-count">0</span>)
            </button>
          </div>
          <table class="user-search-results sortable">
            <thead>
              <tr>
                <th width="40">
                  <input type="checkbox" class="user-search-check-all">
                </th>
                <th class="sortable-column" data-sort="name">
                  Name
                  <span class="sort-indicator">
                    <i class="fas fa-sort"></i>
                  </span>
                </th>
                <th class="sortable-column" data-sort="email">
                  Email
                  <span class="sort-indicator">
                    <i class="fas fa-sort"></i>
                  </span>
                </th>
                <th class="sortable-column" data-sort="role">
                  Role
                  <span class="sort-indicator">
                    <i class="fas fa-sort"></i>
                  </span>
                </th>
                <th class="sortable-column" data-sort="company">
                  Company
                  <span class="sort-indicator">
                    <i class="fas fa-sort"></i>
                  </span>
                </th>
                <th width="60">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colspan="6" class="text-center">
                  <div class="loading-message">Loading users...</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="user-search-pagination"></div>

        ${this.options.showSelectedSection ? this.getSelectedUsersTemplate() : ''}
      </div>
    `;
  }

  getSelectedUsersTemplate () {
    return `
      <div class="user-search-selected-section" style="display: none;">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem;">
          <h4 style="margin: 0;">Selected Users</h4>
          <div style="display: flex; gap: 1rem;">
            <button type="button" class="button small remove-selected-btn" style="display: none;">
              <i class="fas fa-minus"></i> Remove Selected (<span class="remove-selected-count">0</span>)
            </button>
            <button type="button" class="button small secondary clear-selection-btn">
              <i class="fas fa-times"></i> Remove All Users (<span class="total-selected-count">0</span>)
            </button>
          </div>
        </div>
        <div class="selected-users-wrapper">
          <table class="user-search-selected-table">
            <thead>
              <tr>
                <th width="40">
                  <input type="checkbox" class="selected-check-all">
                </th>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Company</th>
                <th width="60">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colspan="6" class="text-center">No users selected</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="user-search-selected-pagination"></div>
      </div>
    `;
  }

  bindEvents () {
    if (!this.container) return;

    // Search input with debounce
    if (this.searchInput) {
      let searchTimeout;
      this.searchInput.addEventListener('input', (e) => {
        clearTimeout(searchTimeout);
        searchTimeout = setTimeout(() => {
          this.searchParams.query = e.target.value;
          this.currentPage = 1;
          this.performSearch();
          this.updateFilterDisplay('search');
        }, 300);
      });
    }

    // Active toggle
    if (this.activeToggle) {
      this.activeToggle.addEventListener('change', (e) => {
        this.searchParams.active = e.target.checked;
        this.currentPage = 1;
        this.performSearch();
      });
    }

    // Clear all filters button
    if (this.clearAllBtn) {
      this.clearAllBtn.addEventListener('click', () => {
        this.clearAllFilters();
      });
    }

    // Filter clear buttons
    this.container.querySelectorAll('.filter-clear').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const filter = e.currentTarget.dataset.filter;
        this.clearFilter(filter);
      });
    });

    // Genre logic toggle
    if (this.genreLogicInputs) {
      this.genreLogicInputs.forEach(input => {
        input.addEventListener('change', (e) => {
          this.searchParams.genreLogic = e.target.value;
          this.currentPage = 1;
          this.performSearch();
        });
      });
    }

    // Sort column clicks
    this.container.querySelectorAll('.sortable-column').forEach(th => {
      th.addEventListener('click', (e) => {
        const column = e.currentTarget.dataset.sort;
        this.handleSort(column);
      });
    });

    // Check all in results
    if (this.checkAll) {
      this.checkAll.addEventListener('change', (e) => {
        const checkboxes = this.resultsTable.querySelectorAll('tbody input[type="checkbox"]');
        const isChecking = e.target.checked;

        checkboxes.forEach(cb => {
          const row = cb.closest('tr');
          // Only check/uncheck if row is visible
          if (row && row.style.display !== 'none' && cb.checked !== isChecking) {
            cb.checked = isChecking;
            this.handleUserSelection(cb);
          }
        });
      });
    }

    // Check all in selected
    if (this.selectedCheckAll) {
      this.selectedCheckAll.addEventListener('change', (e) => {
        const checkboxes = this.selectedTable.querySelectorAll('tbody input[type="checkbox"]');
        const isChecking = e.target.checked;

        checkboxes.forEach(cb => {
          if (cb.checked !== isChecking) {
            cb.checked = isChecking;
            this.handleSelectedUserCheck(cb);
          }
        });
      });
    }

    // Add selected button
    if (this.addSelectedBtn) {
      this.addSelectedBtn.addEventListener('click', () => {
        this.addCheckedUsers();
      });
    }

    // Add all results button
    if (this.addAllResultsBtn) {
      this.addAllResultsBtn.addEventListener('click', () => {
        this.addAllResults();
      });
    }

    // Remove selected button
    if (this.removeSelectedBtn) {
      this.removeSelectedBtn.addEventListener('click', () => {
        this.removeCheckedSelected();
      });
    }

    // Clear selection button
    const clearSelectionBtn = this.container.querySelector('.clear-selection-btn');
    if (clearSelectionBtn) {
      clearSelectionBtn.addEventListener('click', () => {
        this.clearSelection();
      });
    }

    // Pagination clicks
    if (this.pagination) {
      this.pagination.addEventListener('click', (e) => {
        if (e.target.dataset.page) {
          this.currentPage = parseInt(e.target.dataset.page);
          this.performSearch();
        }
      });
    }

    // Selected users pagination clicks
    const selectedPagination = this.container.querySelector('.user-search-selected-pagination');
    if (selectedPagination) {
      selectedPagination.addEventListener('click', (e) => {
        if (e.target.dataset.page) {
          e.preventDefault();
          this.selectedCurrentPage = parseInt(e.target.dataset.page);
          this.updateSelectedUsersTable();
        }
      });
    }

  }

  async loadInitialData () {
    // Load all data in parallel
    await Promise.all([
      this.loadRoles(),
      this.loadGenres(),
      this.loadCompanies(),
    ]);

    // Initialize multi-select plugins
    this.initializeMultiSelects();

    // Perform initial search
    this.performSearch();
  }

  async loadRoles () {
    try {
      const response = await fetch('/api/roles');
      const data = await response.json();

      if (data.success && this.roleSelect) {
        this.roleSelect.innerHTML = '';
        data.data.forEach(role => {
          const option = document.createElement('option');
          option.value = role.id;
          option.textContent = role.name;
          this.roleSelect.appendChild(option);
        });
      }
    } catch (error) {
      console.error('Error loading roles:', error);
    }
  }

  async loadGenres () {
    try {
      const response = await fetch('/api/genres');
      const data = await response.json();

      if (data.success && this.genreSelect) {
        this.genreSelect.innerHTML = '';
        data.data.forEach(genre => {
          const option = document.createElement('option');
          option.value = genre.id;
          option.textContent = genre.name;
          this.genreSelect.appendChild(option);
        });
      }
    } catch (error) {
      console.error('Error loading genres:', error);
    }
  }

  async loadCompanies () {
    try {
      // Request all companies without pagination
      const response = await fetch('/api/companies?per_page=1000');
      const data = await response.json();

      if (data.success && this.companySelect) {
        this.companySelect.innerHTML = '';
        if (Array.isArray(data.data)) {
          data.data.forEach(company => {
            const option = document.createElement('option');
            option.value = company.id;
            option.textContent = company.name || company.c_name || 'Unknown Company';
            this.companySelect.appendChild(option);
          });
        }
      }
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  }

  initializeMultiSelects () {
    // Initialize role multi-select
    if (this.roleSelect && typeof $ !== 'undefined' && $.fn.multiselect) {
      this.multiselectInstances.roles = $(this.roleSelect).multiselect({
        texts: {
          placeholder: 'All Roles',
          selectAll: 'Select All Roles',
          unselectAll: 'Unselect All Roles',
        },
        search: true,
        onOptionClick: () => {
          setTimeout(() => {
            this.searchParams.roles = $(this.roleSelect).val() || [];
            this.currentPage = 1;
            this.performSearch();
            this.updateFilterDisplay('roles');
          }, 0);
        },
      });
    }

    // Initialize company multi-select
    if (this.companySelect && typeof $ !== 'undefined' && $.fn.multiselect) {
      this.multiselectInstances.companies = $(this.companySelect).multiselect({
        texts: {
          placeholder: 'All Companies',
          selectAll: 'Select All Companies',
          unselectAll: 'Unselect All Companies',
        },
        search: true,
        onOptionClick: () => {
          setTimeout(() => {
            this.searchParams.companies = $(this.companySelect).val() || [];
            this.currentPage = 1;
            this.performSearch();
            this.updateFilterDisplay('companies');
          }, 0);
        },
      });
    }

    // Initialize genre multi-select
    if (this.genreSelect && typeof $ !== 'undefined' && $.fn.multiselect) {
      this.multiselectInstances.genres = $(this.genreSelect).multiselect({
        texts: {
          placeholder: 'All Genres',
          selectAll: 'Select All Genres',
          unselectAll: 'Unselect All Genres',
        },
        search: true,
        onOptionClick: () => {
          setTimeout(() => {
            this.searchParams.genres = $(this.genreSelect).val() || [];
            this.currentPage = 1;
            this.performSearch();
            this.updateFilterDisplay('genres');
          }, 0);
        },
      });
    }
  }

  handleSort (column) {
    if (this.sortBy === column) {
      // Toggle direction if same column
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      // New column, default to asc
      this.sortBy = column;
      this.sortDir = 'asc';
    }

    this.updateSortIndicators();
    this.performSearch();
  }

  updateSortIndicators () {
    // Reset all indicators
    this.container.querySelectorAll('.sortable-column').forEach(th => {
      const indicator = th.querySelector('.sort-indicator i');
      indicator.className = 'fas fa-sort';
      th.classList.remove('sorted-asc', 'sorted-desc');
    });

    // Update active column
    const activeColumn = this.container.querySelector(`.sortable-column[data-sort="${this.sortBy}"]`);
    if (activeColumn) {
      const indicator = activeColumn.querySelector('.sort-indicator i');
      indicator.className = this.sortDir === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down';
      activeColumn.classList.add(`sorted-${this.sortDir}`);
    }
  }

  clearFilter (filterName) {
    switch (filterName) {
      case 'search':
        this.searchParams.query = '';
        if (this.searchInput) {
          this.searchInput.value = '';
        }
        break;
      case 'roles':
        this.searchParams.roles = [];
        if (this.multiselectInstances.roles) {
          $(this.roleSelect).val([]);
          $(this.roleSelect).multiselect('reload');
        }
        break;
      case 'companies':
        this.searchParams.companies = [];
        if (this.multiselectInstances.companies) {
          $(this.companySelect).val([]);
          $(this.companySelect).multiselect('reload');
        }
        break;
      case 'genres':
        this.searchParams.genres = [];
        if (this.multiselectInstances.genres) {
          $(this.genreSelect).val([]);
          $(this.genreSelect).multiselect('reload');
        }
        break;
    }
    this.currentPage = 1;
    this.updateFilterDisplay(filterName);
    this.performSearch();
  }

  clearAllFilters () {
    // Clear search
    if (this.searchInput) {
      this.searchInput.value = '';
      this.searchParams.query = '';
    }

    // Clear all filters
    this.searchParams.roles = [];
    this.searchParams.companies = [];
    this.searchParams.genres = [];

    // Reload multiselects
    if (this.multiselectInstances.roles) {
      $(this.roleSelect).multiselect('reload');
    }
    if (this.multiselectInstances.companies) {
      $(this.companySelect).multiselect('reload');
    }
    if (this.multiselectInstances.genres) {
      $(this.genreSelect).multiselect('reload');
    }

    // Reset to active users only
    this.searchParams.active = true;
    if (this.activeToggle) {
      this.activeToggle.checked = true;
    }

    // Clear checked items
    this.checkedResults.clear();
    this.checkedSelected.clear();

    // Update UI state for checked items
    this.updateAddSelectedButton();
    this.updateRemoveSelectedButton();
    this.updateCheckAllState();
    this.updateSelectedCheckAllState();

    // Reset sort
    this.sortBy = 'name';
    this.sortDir = 'asc';
    this.updateSortIndicators();

    // Reset page
    this.currentPage = 1;

    // Update filter displays
    this.updateFilterDisplay('roles');
    this.updateFilterDisplay('companies');
    this.updateFilterDisplay('genres');

    this.performSearch();
  }

  updateFilterDisplay (filterName) {
    const clearBtn = this.container.querySelector(`.filter-clear[data-filter="${filterName}"]`);
    if (!clearBtn) return;

    let shouldShow = false;
    if (filterName === 'search') {
      shouldShow = this.searchParams.query.length > 0;
    } else {
      shouldShow = (this.searchParams[filterName]?.length || 0) > 0;
    }

    clearBtn.style.display = shouldShow ? 'inline-flex' : 'none';
  }

  async performSearch () {
    if (!this.resultsTable) return;

    const tbody = this.resultsTable.querySelector('tbody');
    tbody.innerHTML = '<tr><td colspan="5" class="text-center"><i class="fas fa-spinner fa-spin"></i> Searching...</td></tr>';

    try {
      const params = new URLSearchParams({
        page: this.currentPage,
        per_page: this.options.pageSize,
        sort_by: this.sortBy,
        sort_dir: this.sortDir,
        active: this.searchParams.active,
      });

      // Add search params
      if (this.searchParams.query) {
        params.append('query', this.searchParams.query);
      }

      if (this.searchParams.roles.length > 0) {
        this.searchParams.roles.forEach(roleId => {
          params.append('roles[]', roleId);
        });
      }

      if (this.searchParams.companies.length > 0) {
        this.searchParams.companies.forEach(companyId => {
          params.append('companies[]', companyId);
        });
      }

      // Handle multiple genres with OR/AND logic
      if (this.searchParams.genres && this.searchParams.genres.length > 0) {
        this.searchParams.genres.forEach(genreId => {
          params.append('genres[]', genreId);
        });
        // Send genre logic parameter
        params.append('genre_logic', this.searchParams.genreLogic);
      }

      const response = await fetch(`/api/accounts?${params}`);
      const data = await response.json();

      if (data.success) {
        this.totalResults = data.meta.total;
        this.totalPages = data.meta.total_pages;
        this.renderResults(data.data);
        this.renderPagination();
        this.updateSelectedCount();

        // Update Add All Results button count
        this.updateAddAllResultsCount();
      }
    } catch (error) {
      console.error('Error performing search:', error);
      tbody.innerHTML = '<tr><td colspan="5" class="text-center text-error">Error loading users. Please try again.</td></tr>';
    }
  }

  renderResults (users) {
    const tbody = this.resultsTable.querySelector('tbody');

    if (users.length === 0) {
      tbody.innerHTML = '<tr><td colspan="5" class="text-center">No users found matching your criteria.</td></tr>';
      return;
    }

    tbody.innerHTML = users.map(user => {
      const isSelected = this.selectedUsers.has(user.id);
      const isChecked = this.checkedResults.has(user.id);
      // Hide row if user is already selected
      if (isSelected) {
        return '';
      }
      return `
        <tr data-user-id="${user.id}">
          <td>
            <input type="checkbox"
                   value="${user.id}"
                   ${isChecked ? 'checked' : ''}
                   data-user='${JSON.stringify(user).replace(/'/g, '&apos;')}'>
          </td>
          <td>${user.first_name} ${user.last_name}</td>
          <td>${user.email}</td>
          <td>${user.role_name || 'N/A'}</td>
          <td>${user.company_name || 'N/A'}</td>
          <td>
            <button type="button" class="button small success add-single-user" data-user-id="${user.id}">
              <i class="fas fa-plus"></i>
            </button>
          </td>
        </tr>
      `;
    }).join('');

    // Bind checkbox events
    tbody.querySelectorAll('input[type="checkbox"]').forEach(cb => {
      cb.addEventListener('change', () => this.handleUserSelection(cb));
    });

    // Bind single add buttons
    tbody.querySelectorAll('.add-single-user').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const userId = parseInt(e.currentTarget.dataset.userId);
        const row = e.currentTarget.closest('tr');
        const checkbox = row.querySelector('input[type="checkbox"]');
        if (checkbox) {
          const userData = JSON.parse(checkbox.dataset.user);
          this.selectedUsers.set(userId, userData);

          // Remove from checked results if it was checked
          this.checkedResults.delete(userId);

          // Hide the row
          row.style.display = 'none';

          if (this.options.onSelect) {
            this.options.onSelect(userId, userData);
          }

          this.updateSelectedCount();
          this.updateCheckAllState();
          this.updateSelectedUsersTable();
          this.updateAddSelectedButton();

          // Update Add All Results count
          this.updateAddAllResultsCount();
        }
      });
    });

    // Bind row click events for selection
    tbody.querySelectorAll('tr[data-user-id]').forEach(row => {
      row.addEventListener('click', (e) => {
        // Don't select if clicking on checkbox
        if (e.target.type === 'checkbox') return;

        const checkbox = row.querySelector('input[type="checkbox"]');
        if (!checkbox) return;

        if (e.shiftKey && this.lastClickedRow) {
          // Multi-select with shift
          this.selectRange(this.lastClickedRow, row);
        } else {
          // Single select - toggle checkbox state
          checkbox.checked = !checkbox.checked;
          this.handleUserSelection(checkbox);
        }

        this.lastClickedRow = row;
      });
    });
  }

  renderPagination () {
    if (!this.pagination || this.totalPages <= 1) {
      if (this.pagination) this.pagination.innerHTML = '';
      return;
    }

    let paginationHtml = '<ul class="pagination" style="margin-bottom: 0;">';

    // Previous button
    if (this.currentPage > 1) {
      paginationHtml += `<li class="pa prev"><a href="#" data-page="${this.currentPage - 1}" style="display: flex; align-items: center; justify-content: center; min-height: 2.5rem;">&laquo;</a></li>`;
    }

    // Page numbers
    const maxVisible = 5;
    let startPage = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(this.totalPages, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    if (startPage > 1) {
      paginationHtml += '<li class="pa"><a href="#" data-page="1" style="display: flex; align-items: center; justify-content: center; min-height: 2.5rem;">1</a></li>';
      if (startPage > 2) {
        paginationHtml += '<li class="pa disabled"><a style="display: flex; align-items: center; justify-content: center; min-height: 2.5rem;">&hellip;</a></li>';
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      paginationHtml += `<li class="pa ${i === this.currentPage ? 'active' : ''}">
        <a href="#" data-page="${i}" style="display: flex; align-items: center; justify-content: center; min-height: 2.5rem;">${i}</a>
      </li>`;
    }

    if (endPage < this.totalPages) {
      if (endPage < this.totalPages - 1) {
        paginationHtml += '<li class="pa disabled"><a style="display: flex; align-items: center; justify-content: center; min-height: 2.5rem;">&hellip;</a></li>';
      }
      paginationHtml += `<li class="pa"><a href="#" data-page="${this.totalPages}" style="display: flex; align-items: center; justify-content: center; min-height: 2.5rem;">${this.totalPages}</a></li>`;
    }

    // Next button
    if (this.currentPage < this.totalPages) {
      paginationHtml += `<li class="pa next"><a href="#" data-page="${this.currentPage + 1}" style="display: flex; align-items: center; justify-content: center; min-height: 2.5rem;">&raquo;</a></li>`;
    }

    paginationHtml += '</ul>';

    this.pagination.innerHTML = paginationHtml;

    // Prevent default on pagination links
    this.pagination.querySelectorAll('a').forEach(link => {
      link.addEventListener('click', (e) => e.preventDefault());
    });
  }

  selectRange (startRow, endRow) {
    const tbody = this.resultsTable.querySelector('tbody');
    const rows = Array.from(tbody.querySelectorAll('tr[data-user-id]'));

    const startIndex = rows.indexOf(startRow);
    const endIndex = rows.indexOf(endRow);

    const [minIndex, maxIndex] = [Math.min(startIndex, endIndex), Math.max(startIndex, endIndex)];

    for (let i = minIndex; i <= maxIndex; i++) {
      const row = rows[i];
      const checkbox = row.querySelector('input[type="checkbox"]');
      if (checkbox && !checkbox.checked) {
        checkbox.checked = true;
        this.handleUserSelection(checkbox);
      }
    }
  }

  handleUserSelection (checkbox) {
    const userId = parseInt(checkbox.value);
    const row = checkbox.closest('tr');

    if (checkbox.checked) {
      this.checkedResults.add(userId);
      if (row) row.classList.add('checked-row');
    } else {
      this.checkedResults.delete(userId);
      if (row) row.classList.remove('checked-row');
    }

    this.updateCheckAllState();
    this.updateAddSelectedButton();
  }

  updateSelectedCount () {
    const count = this.selectedUsers.size;

    // Show/hide selected users section
    const selectedSection = this.container.querySelector('.user-search-selected-section');
    if (selectedSection) {
      selectedSection.style.display = count > 0 ? 'block' : 'none';
    }
  }

  updateCheckAllState () {
    if (!this.checkAll) return;

    // Only consider visible checkboxes
    const allCheckboxes = this.resultsTable.querySelectorAll('tbody input[type="checkbox"]');
    const visibleCheckboxes = Array.from(allCheckboxes).filter(cb => {
      const row = cb.closest('tr');
      return row && row.style.display !== 'none';
    });

    const checkedCount = visibleCheckboxes.filter(cb => cb.checked).length;

    this.checkAll.checked = visibleCheckboxes.length > 0 && checkedCount === visibleCheckboxes.length;
    this.checkAll.indeterminate = checkedCount > 0 && checkedCount < visibleCheckboxes.length;
  }

  updateAddSelectedButton () {
    if (!this.addSelectedBtn) return;

    const checkedCount = this.checkedResults.size;
    const selectedCountSpan = this.addSelectedBtn.querySelector('.selected-count');

    if (selectedCountSpan) {
      selectedCountSpan.textContent = checkedCount;
    }

    this.addSelectedBtn.style.display = checkedCount > 0 ? 'inline-flex' : 'none';
  }

  updateRemoveSelectedButton () {
    if (!this.removeSelectedBtn) return;

    const checkedCount = this.checkedSelected.size;
    const removeCountSpan = this.removeSelectedBtn.querySelector('.remove-selected-count');

    if (removeCountSpan) {
      removeCountSpan.textContent = checkedCount;
    }

    this.removeSelectedBtn.style.display = checkedCount > 0 ? 'inline-flex' : 'none';
  }

  updateSelectedUsersTable () {
    if (!this.selectedTable || !this.options.showSelectedSection) return;

    const tbody = this.selectedTable.querySelector('tbody');

    if (this.selectedUsers.size === 0) {
      tbody.innerHTML = '<tr><td colspan="5" class="text-center">No users selected</td></tr>';
      this.renderSelectedPagination();
      return;
    }

    // Calculate pagination for selected users
    const selectedUsersArray = Array.from(this.selectedUsers.entries());
    const totalSelected = selectedUsersArray.length;
    this.selectedTotalPages = Math.ceil(totalSelected / this.selectedPageSize);

    // Ensure current page is within bounds
    if (this.selectedCurrentPage > this.selectedTotalPages) {
      this.selectedCurrentPage = Math.max(1, this.selectedTotalPages);
    }

    // Get users for current page
    const startIndex = (this.selectedCurrentPage - 1) * this.selectedPageSize;
    const endIndex = startIndex + this.selectedPageSize;
    const usersForPage = selectedUsersArray.slice(startIndex, endIndex);

    tbody.innerHTML = usersForPage.map(([userId, user]) => {
      const isChecked = this.checkedSelected.has(userId);
      return `
        <tr data-user-id="${userId}">
          <td>
            <input type="checkbox"
                   value="${userId}"
                   ${isChecked ? 'checked' : ''}
                   data-user='${JSON.stringify(user).replace(/'/g, '&apos;')}'>
          </td>
          <td>${user.first_name} ${user.last_name}</td>
          <td>${user.email}</td>
          <td>${user.role_name || 'N/A'}</td>
          <td>${user.company_name || 'N/A'}</td>
          <td>
            <button type="button" class="button small alert remove-user" data-user-id="${userId}">
              <i class="fas fa-times"></i>
            </button>
          </td>
        </tr>
      `;
    }).join('');

    // Bind remove buttons
    tbody.querySelectorAll('.remove-user').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const userId = parseInt(e.currentTarget.dataset.userId);
        this.deselectUser(userId);
      });
    });

    // Bind checkbox events for selected users
    tbody.querySelectorAll('input[type="checkbox"]').forEach(cb => {
      cb.addEventListener('change', () => this.handleSelectedUserCheck(cb));
    });

    // Bind row click events for selected users table
    tbody.querySelectorAll('tr[data-user-id]').forEach(row => {
      row.addEventListener('click', (e) => {
        // Don't toggle if clicking on checkbox, button, or link
        if (e.target.type === 'checkbox' || e.target.tagName === 'BUTTON' || e.target.tagName === 'A' || e.target.closest('button')) return;

        const checkbox = row.querySelector('input[type="checkbox"]');
        if (checkbox) {
          checkbox.checked = !checkbox.checked;
          this.handleSelectedUserCheck(checkbox);
        }
      });
    });

    // Update button count
    const totalCountSpan = this.container.querySelector('.total-selected-count');
    if (totalCountSpan) {
      totalCountSpan.textContent = this.selectedUsers.size;
    }

    // Render pagination for selected users
    this.renderSelectedPagination();
  }

  deselectUser (userId) {
    const userData = this.selectedUsers.get(userId);
    this.selectedUsers.delete(userId);

    // Show row in main table if it should be visible
    const checkbox = this.resultsTable.querySelector(`input[value="${userId}"]`);
    if (checkbox) {
      checkbox.checked = false;
      const row = checkbox.closest('tr');
      if (row) {
        row.style.display = '';
      }
    }

    // Remove from checked results
    this.checkedResults.delete(userId);
    this.checkedSelected.delete(userId);

    this.updateSelectedCount();
    this.updateCheckAllState();
    this.updateSelectedUsersTable();
    this.updateAddSelectedButton();
    this.updateRemoveSelectedButton();
    this.updateAddAllResultsCount();

    if (this.options.onDeselect) {
      this.options.onDeselect(userId, userData);
    }
  }

  renderSelectedPagination () {
    const selectedPagination = this.container.querySelector('.user-search-selected-pagination');
    if (!selectedPagination || this.selectedTotalPages <= 1) {
      if (selectedPagination) selectedPagination.innerHTML = '';
      return;
    }

    let paginationHtml = '<ul class="pagination" style="margin-bottom: 0;">';

    // Previous button
    if (this.selectedCurrentPage > 1) {
      paginationHtml += `<li class="pa prev"><a href="#" data-page="${this.selectedCurrentPage - 1}" style="display: flex; align-items: center; justify-content: center; min-height: 2.5rem;">&laquo;</a></li>`;
    }

    // Page numbers
    const maxVisible = 5;
    let startPage = Math.max(1, this.selectedCurrentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(this.selectedTotalPages, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    if (startPage > 1) {
      paginationHtml += '<li class="pa"><a href="#" data-page="1" style="display: flex; align-items: center; justify-content: center; min-height: 2.5rem;">1</a></li>';
      if (startPage > 2) {
        paginationHtml += '<li class="pa disabled"><a style="display: flex; align-items: center; justify-content: center; min-height: 2.5rem;">&hellip;</a></li>';
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      paginationHtml += `<li class="pa ${i === this.selectedCurrentPage ? 'active' : ''}">
        <a href="#" data-page="${i}" style="display: flex; align-items: center; justify-content: center; min-height: 2.5rem;">${i}</a>
      </li>`;
    }

    if (endPage < this.selectedTotalPages) {
      if (endPage < this.selectedTotalPages - 1) {
        paginationHtml += '<li class="pa disabled"><a style="display: flex; align-items: center; justify-content: center; min-height: 2.5rem;">&hellip;</a></li>';
      }
      paginationHtml += `<li class="pa"><a href="#" data-page="${this.selectedTotalPages}" style="display: flex; align-items: center; justify-content: center; min-height: 2.5rem;">${this.selectedTotalPages}</a></li>`;
    }

    // Next button
    if (this.selectedCurrentPage < this.selectedTotalPages) {
      paginationHtml += `<li class="pa next"><a href="#" data-page="${this.selectedCurrentPage + 1}" style="display: flex; align-items: center; justify-content: center; min-height: 2.5rem;">&raquo;</a></li>`;
    }

    paginationHtml += '</ul>';

    selectedPagination.innerHTML = paginationHtml;
  }

  handleSelectedUserCheck (checkbox) {
    const userId = parseInt(checkbox.value);
    const row = checkbox.closest('tr');

    if (checkbox.checked) {
      this.checkedSelected.add(userId);
      if (row) row.classList.add('checked-row');
    } else {
      this.checkedSelected.delete(userId);
      if (row) row.classList.remove('checked-row');
    }

    this.updateRemoveSelectedButton();
    this.updateSelectedCheckAllState();
  }

  updateSelectedCheckAllState () {
    if (!this.selectedCheckAll) return;

    const checkboxes = this.selectedTable.querySelectorAll('tbody input[type="checkbox"]');
    const checkedCount = Array.from(checkboxes).filter(cb => cb.checked).length;

    this.selectedCheckAll.checked = checkboxes.length > 0 && checkedCount === checkboxes.length;
    this.selectedCheckAll.indeterminate = checkedCount > 0 && checkedCount < checkboxes.length;
  }

  addCheckedUsers () {
    const checkboxes = this.resultsTable.querySelectorAll('tbody input[type="checkbox"]:checked');

    checkboxes.forEach(cb => {
      const userId = parseInt(cb.value);
      const userData = JSON.parse(cb.dataset.user);

      this.selectedUsers.set(userId, userData);
      this.checkedResults.delete(userId);

      // Hide the row instead of disabling
      const row = cb.closest('tr');
      if (row) {
        row.style.display = 'none';
      }

      if (this.options.onSelect) {
        this.options.onSelect(userId, userData);
      }
    });

    this.updateSelectedCount();
    this.updateCheckAllState();
    this.updateSelectedUsersTable();
    this.updateAddSelectedButton();
    this.updateAddAllResultsCount();
  }

  async addAllResults () {
    // Show loading state
    const originalText = this.addAllResultsBtn.innerHTML;
    this.addAllResultsBtn.disabled = true;
    this.addAllResultsBtn.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Adding all users...';

    try {
      // Build the same query parameters as the current search
      const params = new URLSearchParams({
        per_page: 100, // Fetch in larger chunks for efficiency
        sort_by: this.sortBy,
        sort_dir: this.sortDir,
        active: this.searchParams.active,
      });

      // Add search params
      if (this.searchParams.query) {
        params.append('query', this.searchParams.query);
      }

      if (this.searchParams.roles.length > 0) {
        this.searchParams.roles.forEach(roleId => {
          params.append('roles[]', roleId);
        });
      }

      if (this.searchParams.companies.length > 0) {
        this.searchParams.companies.forEach(companyId => {
          params.append('companies[]', companyId);
        });
      }

      if (this.searchParams.genres && this.searchParams.genres.length > 0) {
        this.searchParams.genres.forEach(genreId => {
          params.append('genres[]', genreId);
        });
        params.append('genre_logic', this.searchParams.genreLogic);
      }

      // Fetch all pages
      let currentPage = 1;
      let hasMorePages = true;
      let totalAdded = 0;

      while (hasMorePages) {
        params.set('page', currentPage);

        const response = await fetch(`/api/accounts?${params}`);
        const data = await response.json();

        if (data.success && data.data.length > 0) {
          // Add all users from this page
          data.data.forEach(user => {
            // Skip if user is already selected
            if (!this.selectedUsers.has(user.id)) {
              this.selectedUsers.set(user.id, user);
              totalAdded++;

              if (this.options.onSelect) {
                this.options.onSelect(user.id, user);
              }
            }
          });

          // Check if there are more pages
          hasMorePages = currentPage < data.meta.total_pages;
          currentPage++;

          // Update button text with progress
          this.addAllResultsBtn.innerHTML = `<i class="fas fa-spinner fa-spin"></i> Adding users... (${totalAdded} added)`;
        } else {
          hasMorePages = false;
        }
      }

      // Clear all checked items since we added everything
      this.checkedResults.clear();

      // Hide all rows in the current view since they're all selected now
      const rows = this.resultsTable.querySelectorAll('tbody tr[data-user-id]');
      rows.forEach(row => {
        row.style.display = 'none';
      });

      // Update UI
      this.updateSelectedCount();
      this.updateCheckAllState();
      this.updateSelectedUsersTable();
      this.updateAddSelectedButton();
      this.updateAddAllResultsCount();

      // Refresh the current page to show remaining users
      await this.performSearch();

      // Show success message briefly
      this.addAllResultsBtn.innerHTML = `<i class="fas fa-check"></i> Added ${totalAdded} users!`;
      setTimeout(() => {
        this.addAllResultsBtn.innerHTML = originalText;
        this.addAllResultsBtn.disabled = false;
      }, 2000);

    } catch (error) {
      console.error('Error adding all results:', error);
      this.addAllResultsBtn.innerHTML = originalText;
      this.addAllResultsBtn.disabled = false;
      alert('Error adding all users. Please try again.');
    }
  }

  removeCheckedSelected () {
    const checkedUserIds = Array.from(this.checkedSelected);

    checkedUserIds.forEach(userId => {
      this.deselectUser(userId);
      this.checkedSelected.delete(userId);
    });

    this.updateRemoveSelectedButton();
    this.updateSelectedCheckAllState();
  }

  updateAddAllResultsCount () {
    if (!this.addAllResultsBtn) return;

    const countSpan = this.addAllResultsBtn.querySelector('.all-results-count');
    if (countSpan) {
      // Show the total results minus already selected users
      const totalUnselected = this.totalResults - this.selectedUsers.size;
      countSpan.textContent = Math.max(0, totalUnselected);
    }
  }

  // Public API methods for programmatic control
  setGenres (genreIds) {
    this.searchParams.genres = Array.isArray(genreIds) ? genreIds : [genreIds];
    if (this.multiselectInstances.genres && genreIds.length > 0) {
      $(this.genreSelect).val(this.searchParams.genres);
      $(this.genreSelect).multiselect('reload');
    }
    this.updateFilterDisplay('genres');
    this.performSearch();
  }

  setCompanies (companyIds) {
    this.searchParams.companies = Array.isArray(companyIds) ? companyIds : [companyIds];
    if (this.multiselectInstances.companies && companyIds.length > 0) {
      $(this.companySelect).val(this.searchParams.companies);
      $(this.companySelect).multiselect('reload');
    }
    this.updateFilterDisplay('companies');
    this.performSearch();
  }

  setRoles (roleIds) {
    this.searchParams.roles = Array.isArray(roleIds) ? roleIds : [roleIds];
    if (this.multiselectInstances.roles && roleIds.length > 0) {
      $(this.roleSelect).val(this.searchParams.roles);
      $(this.roleSelect).multiselect('reload');
    }
    this.updateFilterDisplay('roles');
    this.performSearch();
  }

  setQuery (query) {
    this.searchParams.query = query;
    if (this.searchInput) {
      this.searchInput.value = query;
    }
    this.performSearch();
  }

  getSelectedUsers () {
    return Array.from(this.selectedUsers.entries()).map(([id, data]) => ({
      id,
      ...data,
    }));
  }

  getSelectedUserIds () {
    return Array.from(this.selectedUsers.keys());
  }

  clearSelection () {
    this.selectedUsers.clear();
    this.checkedResults.clear();
    this.checkedSelected.clear();

    // Show all rows and uncheck all checkboxes
    const rows = this.resultsTable.querySelectorAll('tbody tr[data-user-id]');
    rows.forEach(row => {
      row.style.display = '';
      const checkbox = row.querySelector('input[type="checkbox"]');
      if (checkbox) {
        checkbox.checked = false;
      }
    });

    this.updateSelectedCount();
    this.updateCheckAllState();
    this.updateSelectedUsersTable();
    this.updateAddSelectedButton();
    this.updateRemoveSelectedButton();
    this.updateAddAllResultsCount();
  }

  showModal () {
    if (this.options.isModal && this.container) {
      $(this.container).foundation('open');
    }
  }

  hideModal () {
    if (this.options.isModal && this.container) {
      $(this.container).foundation('close');
    }
  }

  refresh () {
    this.performSearch();
  }
}

// Export UserSearch class to global namespace
window.sflow = window.sflow || {};
window.sflow.UserSearch = UserSearch;

// Auto-initialize for embedded instances
document.addEventListener('DOMContentLoaded', () => {
  const containers = document.querySelectorAll(UserSearch.EL_CONTAINER);
  containers.forEach(container => {
    if (!container.dataset.userSearchInitialized) {
      const instance = new UserSearch({ container });

      // Store instance reference on the container
      container.dataset.userSearchInitialized = 'true';
      container.userSearchInstance = instance;
    }
  });
});
